import React from 'react';
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HtmlRenderer } from './HtmlRenderer';
import { AppColors, typographyClamps } from '../utils/utils';
import { useNavigate } from 'react-router-dom';

interface Feature {
  title: string;
  description: string;
}

interface SectionViewProps {
  idx: number;
  label: string;
  title?: string;
  subtitle?: string;
  features: Feature[];
  footerText: string;
  imagePath: string;
  labelImagePath?: string;
  imageBGPath?: string;
  imageAlign?: 'start' | 'center' | 'end';
  isForFeature?: boolean;
  titleTextColor?: string;
  transparentBackground?: boolean;
  openImageViewView?: (link: string) => void;
}

export const HomeSectionView: React.FC<SectionViewProps> = ({
  idx,
  label,
  title,
  subtitle,
  features,
  footerText,
  imagePath,
  labelImagePath,
  imageBGPath,
  imageAlign,
  isForFeature = true,
  titleTextColor,
  transparentBackground,
  openImageViewView,
}) => {
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const isImageOnRight = idx % 2 !== 0;

  const gridSize = isForFeature
    ? { xs: 12, sm: 5.5, md: 5.9 }
    : { xs: 5.5, sm: 5.5, md: 5.9 };

  const handleGetStartedClick = () => {
    navigate('/pricing');
  };

  const getScreenshotView = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: imageAlign ?? 'center',
        // alignItems: isImageOnRight ? 'flex-end' : 'flex-start',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {imageBGPath && (
        <Box
          component='img'
          src={imageBGPath}
          sx={{
            position: 'absolute',
            display: 'block',
            right: {
              sm: 0,
              md: isImageOnRight ? (imageAlign === 'end' ? '-60%' : '-30%') : 0,
            },
            left: { sm: 0, md: isImageOnRight ? 0 : '-30%' },
            height: isMobile ? '100%' : '100%',
            width: isMobile ? '100%' : 'auto',
            // rotate: idx === 1 ? '30deg' : idx === 2 ? '-150deg' : '0deg',
          }}
        />
      )}

      <Box
        component='img'
        src={imagePath}
        maxWidth={{ xs: '90%', sm: '100%', lg: '70%' }}
        height={'auto'}
        sx={{ cursor: 'pointer'}}
        onClick={(e) => {
          openImageViewView && openImageViewView((e.currentTarget as HTMLImageElement).src);
        }}
      />
    </Box>
  );

  const getFeatureListView = (list: Feature[]) => {
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='start'
      >
        {list.map((item, index) => (
          <Stack
            key={index}
            direction='row'
            alignItems='start'
            py={isMobile ? 0.5 : 1}
            pl={1}
          >
            <Box
              component='img'
              src='/static/images/ic_ai_star.png'
              // height='32px'
              // width='32px'
              height={{ xs: '24px', sm: typographyClamps.fontSize24 }}
              width={{ xs: '24px', sm: typographyClamps.fontSize24 }}
              pt={1}
            />
            {/* )} */}
            <Box sx={{ pl: isMobile ? 1 : 2 }}>
              <Typography
                variant='body1'
                sx={{
                  color: titleTextColor ?? AppColors.grayColor2,
                  fontSize: isMobile
                    ? typographyClamps.h5
                    : typographyClamps.fontSize17,
                  fontWeight: 'bold',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: titleTextColor ?? AppColors.grayColor2,
                  fontSize: isMobile
                    ? typographyClamps.h5
                    : typographyClamps.fontSize17,
                  fontWeight: 500,
                }}
              >
                {item.description}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 0,
        background: isImageOnRight
          ? AppColors.whiteColor
          : AppColors.lightBlueTransparentColor,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        my: 4,
        py: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: isImageOnRight ? 'start' : 'end',
          alignItems: 'center',
        }}
      >
        {!isImageOnRight && (
          <Box
            component={'img'}
            src={labelImagePath}
            height={{ xs: '80px', sm: '150px' }}
            sx={{
              position: 'relative',
              left: 5,
            }}
          />
        )}

        <Box
          sx={{
            bgcolor: AppColors.vividBlueColor2,
            px: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? typographyClamps.h5 : typographyClamps.h4,
              color: 'white',
            }}
          >
            <HtmlRenderer htmlString={label} />
          </Typography>
        </Box>

        {isImageOnRight && (
          <Box
            component={'img'}
            src={labelImagePath}
            height={{ xs: '80px', sm: '150px' }}
            sx={{
              position: 'relative',
              right: 5,
            }}
          />
        )}
      </Box>

      <Grid
        container
        gap={{ sx: 0, sm: 5, md: 1 }}
        direction='row'
        justifyContent='space-between'
        alignItems={'center'}
        // px={{ xs: 2, sm: '5%', md: '8%', lg: '10%' }}
      >
        {/* {!isMobile && !isImageOnRight && ( */}
        {!(isForFeature && isMobile) && !isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}

        <Grid size={gridSize}>
          <Box
            width={'100%'}
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
          >
            {title && (
              <Typography
                component='p'
                sx={{
                  // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                  fontSize: {
                    xs: typographyClamps.caption,
                    sm: typographyClamps.h6,
                    md: typographyClamps.h5,
                  },
                  fontWeight: 'bold',
                  color: titleTextColor ?? AppColors.whiteColor,
                  textAlign: isMobile ? 'center' : 'start',
                  width: '100%',
                  my: 1,
                  '& span': {
                    background: AppColors.textGradient,
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  },
                }}
              >
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography
                component='p'
                textAlign={{ xs: 'center', sm: 'center', md: 'start' }}
                fontWeight={400}
                py={1}
                color='#6B727A'
              >
                <HtmlRenderer htmlString={subtitle} />
              </Typography>
            )}

            {isForFeature && isMobile && (
              <Box py={2}>{getScreenshotView()}</Box>
            )}

            {features && getFeatureListView(features)}

            {footerText && (
              <Typography
                sx={{ py: 1, color: AppColors.redColor, fontStyle: 'italic' }}
              >
                {footerText}
              </Typography>
            )}

            {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            {isForFeature ? 'Learn More' : 'Get Started'}
          </GradientButton> */}
          </Box>
        </Grid>

        {/* {!isMobile && isImageOnRight && ( */}
        {!(isForFeature && isMobile) && isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          px: 3,
          position: 'relative',
        }}
      >
        {/* <Box
          component={'img'}
          src='/static/home_page/img_georgraphy_pattern.png'
          sx={{
            position: 'absolute',
            overflow: 'hidden',
            ...(isImageOnRight ? { left: '-50%' } : { right: '-50%' }),
          }}
        /> */}

        <Box
          sx={{
            width: 300,
            height: 200,
            overflow: 'hidden',
            position: 'absolute',
            right: isImageOnRight ? undefined : 0,
            left: !isImageOnRight ? undefined : 0,
            display: { xs: 'none', sm: 'none', md: 'block' },
          }}
        >
          <Box
            component='img'
            src='/static/home_page/img_georgraphy_pattern.png'
            sx={
              {
                // width: '100%',
                // height: '100%',
                // objectFit: 'none', // key
              }
            }
          />
        </Box>

        <Button
          variant='contained'
          sx={{
            borderRadius: 0,
            bgcolor: AppColors.orangeBGColor,
            color: 'white',
            textTransform: 'none',
            padding: '8px 24px',
            fontSize: typographyClamps.h6,
            whiteSpace: 'pre-line',
            lineHeight: 1.35,
            my: { xs: 2, sm: 8 },
            '&:hover': {
              borderRadius: 0,
            },
          }}
          onClick={() => {
            // onOpenRequestDemoModel();
          }}
        >
          Learn More
        </Button>
        <Button
          variant='outlined'
          sx={{
            borderRadius: 0,
            borderColor: AppColors.vividBlueColor2,
            borderWidth: 2,
            color: AppColors.vividBlueColor2,
            backgroundColor: 'transparent',
            textTransform: 'none',
            padding: '8px 24px',
            fontSize: typographyClamps.h6,
            whiteSpace: 'pre-line',
            lineHeight: 1.35,
            my: { xs: 2, sm: 8 },
            '&:hover': {
              borderColor: AppColors.vividBlueColor2,
              backgroundColor: 'rgba(0, 0, 0, 0.04)', // optional subtle hover
              borderRadius: 0,
            },
          }}
          onClick={() => {
            handleGetStartedClick();
          }}
        >
          Get Started
        </Button>
      </Box>
    </Box>
  );
};
